import React, { Fragment } from 'react'
import { Chapter } from '../drilldown-content-view'
import { WithTooltip } from '../../../../design-system'
import { FormattedMessage } from 'react-intl'
import { MustacheRenderer } from '../../../../legacy-design-system/mustache'
import { css } from '@emotion/css'
import { DownloadAttackValueLink } from '../../cell-content/download-attack-value-link'
import { theme } from '@contrast-security-inc/design-system-foundations'

const bodyTemplateCharLimit = 500

interface ChapterViewProps {
  readonly organizationID: string
  readonly eventId: string
  readonly hidden: boolean
  readonly ruleUuid: string
  readonly chapter: Chapter
}

export function MustacheChapterView({
  organizationID,
  eventId,
  hidden,
  ruleUuid,
  chapter
}: ChapterViewProps) {
  if (hidden && chapter.introTemplate) {
    return (
      <WithTooltip
        placement="right"
        renderTooltipContent={() => (
          <FormattedMessage id="detail_attackEventDetail_applyLicenseToSeeDetails" />
        )}
        children={() => (
          <ChapterIntroText
            introTemplate={chapter.introTemplate}
            introVariables={chapter.introVariables}
          />
        )}
      />
    )
  }
  return (
    <div>
      {chapter.introTemplate && (
        <ChapterIntroText
          introTemplate={chapter.introTemplate}
          introVariables={chapter.introVariables}
        />
      )}
      {chapter.bodyTemplate && (
        <ChapterBody
          organizationID={organizationID}
          eventId={eventId}
          ruleUuid={ruleUuid}
          chapter={chapter}
        />
      )}
    </div>
  )
}

function ChapterBody({ organizationID, eventId, ruleUuid, chapter }) {
  return (
    <Fragment>
      {chapter.type === 'dataflow' && ruleUuid === 'redos' && (
        <ChapterDataflowRedos
          organizationID={organizationID}
          eventId={eventId}
          chapter={chapter}
        />
      )}
      {(chapter.type !== 'dataflow' || ruleUuid !== 'redos') && (
        <div className="well evidence-well break-word">
          <MustacheRenderer
            tagName="span"
            template={chapter.bodyTemplate}
            views={chapter.bodyVariables}
          />
        </div>
      )}
    </Fragment>
  )
}

function ChapterIntroText({
  introTemplate,
  introVariables
}: {
  readonly introTemplate?: string
  readonly introVariables?: {
    readonly [key: string]: any
  }
}) {
  if (introTemplate) {
    return (
      <div
        className={css`
          word-break: break-word !important;
          word-wrap: break-word !important;
        `}
      >
        <MustacheRenderer
          tagName="span"
          template={introTemplate}
          views={introVariables}
        />
      </div>
    )
  }
  return null
}

function ChapterDataflowRedos({
  organizationID,
  eventId,
  chapter
}: {
  readonly organizationID: string
  readonly eventId: string
  readonly chapter: {
    readonly bodyTemplate?: string
    readonly bodyVariables?: {
      readonly [key: string]: any
    }
  }
}) {
  if (!chapter.bodyTemplate) {
    return null
  }
  return (
    <div>
      {!!chapter.bodyTemplate && (
        <div className="well evidence-well">
          <div
            className={css`
              word-break: break-all !important;
              word-wrap: break-word !important;
            `}
          >
            <MustacheRenderer
              tagName="span"
              template={chapter.bodyTemplate}
              views={chapter.bodyVariables}
              truncate={bodyTemplateCharLimit}
            />
          </div>
          {chapter.bodyTemplate.length > bodyTemplateCharLimit && (
            <div
              className={css`
                max-width: fit-content;
                a {
                  color: ${theme.color.turquoise70} !important;
                }
              `}
            >
              <DownloadAttackValueLink
                organizationID={organizationID}
                eventUuid={eventId}
                showTooltip
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
